import {h, Fragment} from "preact"
import { AutocompleteElement } from "@nosto/preact"
import { useEffect, useState, useMemo } from "preact/hooks";
import { formatCurrency, productImagePlaceholder, getSrcset, toggleQuickAdd, getProductBadges, jsonParse } from "../helpers"
import { productCarouselOptions } from "../config"

import Carousel from "./Carousel"

export default function DropdownProduct({ product }) {
    const [ swatches, setSwatches ] = useState([]);
    const badges = useMemo(() => getProductBadges(product?.tags1), []);
    const promoText = useMemo(() => product.customFields.find(field => field.key === 'product-promotional_copy')?.value, []);
    
    const carouselImages = useMemo(() => {
        return (product.alternateImageUrls?.length > 1)
            ? [ product.imageUrl, ...product.alternateImageUrls ]
            : [ product.imageUrl ]
    }, []);

    useEffect(() => {
        const parsedColors = jsonParse(product.customFields?.find(field => field.key === 'product-available_colors')?.value);

        const formattedSwatches = (parsedColors?.length) 
            ?   parsedColors.reduce((acc, c) => {
                    const obj = Object.values(c)[0];
                    if(obj.image != null){
                        acc.push(obj);
                    }
                    return acc;
                }, []) 
            :   [];
        setSwatches(formattedSwatches);
    }, []);

    return (
        <AutocompleteElement hit={product} class="" >
            <div
                class="nosto-product-card"
                data-url={product.url}
                data-nosto-element="product"
                data-handle={(product.url).toString().split("products/").pop()}
            >
                <a class="nosto-product-link" href={product.url} style="display:none;">{product.name}</a>
                <div className="ns-product group relative cursor-pointer">
                    <div className="relative">
                        <div
                            className="relative group block no-underline mb-2 lg:mb-0 aspect-[2/3]" 
                        >
                            {(carouselImages.length > 1) && (
                                <Carousel
                                    options={productCarouselOptions}
                                    carouselClass="product-images flex lg:hidden"
                                    wrapperUniqueClass={`ns-autocomplete-images-${product.productId}`} 
                                    init={window.innerWidth < 1023 && carouselImages?.length > 1}
                                >
                                    {carouselImages.map(image => (
                                        <div className="swiper-slide flex justify-center items-center" key={image}>
                                            <img
                                                className="w-full h-full object-cover"
                                                srcSet={getSrcset(image, [1, 640])}
                                                sizes="(max-width: 1023px) 640px, 1px"
                                                alt={product.name}
                                            /> 
                                        </div>
                                    ))}
                                </Carousel>
                            )}
                            <span className="w-full h-full relative hidden lg:block">
                                <img
                                    className="image object-cover h-full"
                                    srcset={product.imageUrl
                                        ? getSrcset(product.imageUrl, [1, 640])
                                        : productImagePlaceholder
                                    }
                                    sizes="(min-width: 1024px) 640px, 0vw"
                                    alt={product.name}
                                    title={product.name}
                                    loading="lazy"
                                />
                            </span>
                            {/* wishlist btn */}
                            <rivo-favorite-button
                                data-product-id={product.productId}
                                rivo-ax-data=""
                                rivo-ax-cloak=""
                                rivo-ax-html="$store.icons.heart"
                                rivo-ax-show="$store.rivo_aw_settings?.favorites_enabled &amp;&amp; (!$store.rivo_profile_customer || $store.loading_states.global_favorite_buttons)"
                            />
                        </div>
                        <div className="pointer-events-auto cursor-pointer w-[50px] h-[50px] flex items-end justify-start absolute left-0 bottom-0 pl-3 pb-3 lg:hidden z-1">
                            <button
                                type="button"
                                className="flex justify-center items-center w-4 h-4 rounded-full bg-white"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopImmediatePropagation();
                                    toggleQuickAdd(product.url.split('/products/')[1])
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path stroke="#565351" stroke-linecap="round" d="M5 1v8M9 5H1"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                    {/* BADGES */}
                    {badges?.length > 0 && 
                        <div className="flex flex-wrap gap-x-2 md:gap-2 h8 relative md:absolute md:top-1 md:left-3.5 mx-auto mt-2 md:mr-3.5">
                            {badges.map(badge => (
                                <span className="block w-[fit-content] text-left px-1 py-[0.188rem] md:px-2 md:py-0.5 bg-gray-0/80">
                                    {badge}
                                </span>
                            ))}
                        </div>
                    }
                    <div className="text-center pb-2.5">
                        <div
                            className="no-underline" 
                        >
                            <div className="ns-product-brand text-sm font-medium uppercase text-center lg:mt-2.5">{product.brand}</div>
                            <h6 className="ns-product-name text-sm font-normal capitalize text-center">{product.name}</h6>
                        </div>
                        <p className="h6 text-text-subdued nosto-price">
                            {(product.listPrice && product.listPrice > product.price) ? (
                                <>
                                    <span className="visually-hidden sr-only">Original Price:</span>
                                    <del className="text-gray-600 mr-2 nosto-lp">{formatCurrency(product.listPrice)}</del>
                                    <span className="visually-hidden sr-only">Final Price:</span>
                                    <span className="text-sale nosto-p">{formatCurrency(product.price)}</span>
                                </>
                            ) : (
                                <>
                                    <span className="visually-hidden sr-only">Regular Price:</span>
                                    <span className="nosto-p">{formatCurrency(product.price)}</span>
                                </>
                            )}
                        </p>
                        {promoText && 
                            <h5 class="h7 text-core-3">{promoText}</h5>
                        }
                    </div>
                </div>
                {/* SWATCHES */}
                {(swatches?.length > 0) && (
                    <div className="pb-2.5">
                        <div className="w-full flex gap-3.5 lg:gap-4 h-fit items-center justify-center">
                            {swatches.slice(0, 4).map(swatch => (
                                <div
                                    className="swatch rounded-full w-3 h-3 md:w-4 md:h-4 block cursor-pointer"
                                    style={{
                                        backgroundImage: swatch.image ? `url(${swatch.image}&width=50)` : 'none' 
                                    }}
                                ></div>
                            ))}
                            {(swatches.length > 4) && (
                                <div
                                    className="w-[18px] h-[18px] inline-flex justify-center items-center body-3 leading-none lg:body-2 no-underline"
                                >
                                    +{(swatches.length - 4)}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AutocompleteElement>
    )
}
